/** Мост в Telegram Mini App.
 *
 * SDK кладётся на страницу отдельным файлом (telegram.org у части провайдеров
 * недоступен, поэтому он лежит у нас локально), а типов к нему нет. Описываем
 * ровно то, чем пользуемся, — остальное в объекте нас не касается.
 *
 * Всё обёрнуто в try: старый клиент Telegram может не знать нового метода, и
 * падать из-за отсутствующей вибрации приложение не должно.
 */

type Haptic = 'light' | 'medium' | 'heavy' | 'success' | 'error' | 'warning';

interface TelegramWebApp {
  initData: string;
  /** Нужны только экрану диагностики: старый webview объясняет половину бед */
  platform?: string;
  version?: string;
  ready(): void;
  expand(): void;
  close(): void;
  setHeaderColor?(color: string): void;
  setBackgroundColor?(color: string): void;
  showConfirm?(message: string, callback: (ok: boolean) => void): void;
  openTelegramLink?(url: string): void;
  BackButton?: {
    show(): void;
    hide(): void;
    onClick(handler: () => void): void; offClick(handler: () => void): void;
  };
  HapticFeedback?: {
    impactOccurred(style: string): void;
    notificationOccurred(type: string): void;
    selectionChanged(): void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export const tg = (): TelegramWebApp | undefined => window.Telegram?.WebApp;

export const initData = (): string => tg()?.initData ?? '';

/** Короткий отклик на действие. Без него интерфейс кажется неживым. */
export function tap(kind: Haptic = 'light'): void {
  try {
    const haptic = tg()?.HapticFeedback;
    if (!haptic) return;
    if (kind === 'success' || kind === 'error' || kind === 'warning') {
      haptic.notificationOccurred(kind);
    } else {
      haptic.impactOccurred(kind);
    }
  } catch {
    /* старый клиент — не беда */
  }
}

/** Щелчок выбора: палец протащил стекляшку мимо соседней вкладки.
 *
 * Отдельно от tap: это не удар по кнопке, а щелчок трещотки — тот же отклик,
 * что даёт барабан выбора даты. На ощупь разница большая, и без неё
 * перетаскивание кажется скольжением по стеклу, а не по делениям.
 */
export function pick(): void {
  try {
    tg()?.HapticFeedback?.selectionChanged();
  } catch {
    /* старый клиент — не беда */
  }
}

/** Подтверждение: родное в Telegram, обычное в браузере. */
export function confirmAction(message: string): Promise<boolean> {
  return new Promise((resolve) => {
    const app = tg();
    if (app?.showConfirm) app.showConfirm(message, resolve);
    else resolve(window.confirm(message));
  });
}

/** Раскрыть приложение, покрасить хром под наш фон и выключить зум.
 *
 * Зум гасим руками: meta viewport на iOS не удерживает масштаб, а щипок
 * посреди выбора вкуса превращает приложение в перекошенный документ.
 */
export function initTelegram(): void {
  const app = tg();
  try {
    app?.ready();
    app?.expand();
    app?.setHeaderColor?.('#0b1622');
    app?.setBackgroundColor?.('#0b1622');
  } catch {
    /* браузер вне Telegram — это нормально для отладки */
  }

  for (const event of ['gesturestart', 'gesturechange', 'gestureend']) {
    document.addEventListener(event, (e) => e.preventDefault(), { passive: false });
  }
  document.addEventListener(
    'touchmove',
    (e) => {
      if (e.touches.length > 1) e.preventDefault();
    },
    { passive: false },
  );
}

/** Диалог с менеджером открываем внутри Telegram, а не в браузере.
 *
 * Обычная ссылка на телефоне выкидывает человека наружу и обратно — это
 * лишние секунды ровно там, где он собрался писать про заказ.
 */
export function openManager(url: string): void {
  const app = tg();
  try {
    if (app?.openTelegramLink && url.startsWith('https://t.me/')) {
      app.openTelegramLink(url);
      return;
    }
  } catch {
    /* старый клиент — уходим на обычное открытие */
  }
  window.open(url, '_blank');
}

/** Текущий обработчик системной «назад»: SDK копит все переданные ему
    функции, и без снятия прежней один тап делал несколько шагов назад —
    в том числе по стеку другой вкладки. */
let backHandler: (() => void) | null = null;

export function backButton(visible: boolean, onClick?: () => void): void {
  const button = tg()?.BackButton;
  if (!button) return;
  try {
    if (backHandler && backHandler !== onClick) button.offClick(backHandler);
    if (onClick && onClick !== backHandler) button.onClick(onClick);
    backHandler = onClick ?? null;
    if (visible) button.show();
    else button.hide();
  } catch {
    /* см. выше */
  }
}
